import React from 'react';
import {Button, Flex} from '@chakra-ui/react';
import {ChevronLeftIcon} from '@chakra-ui/icons';
import {
  FiltresItemCatalogue,
  capitalizeFirstLetter,
  useSearchClient,
} from 'ff-common';
import {HookUseTypedFilters} from '@synako/typed-path';

interface CatalogHeaderCategoryFiltersBarProps {
  catWeb: string;
  filtersHook: HookUseTypedFilters<FiltresItemCatalogue>;
  facets?: Record<string, {values: Record<string, number>}>;
}

/**
 * Barre de sous-catégories web pour le catalogue FF.
 * Affiche les enfants de la catégorie courante,
 * avec un bouton retour vers le parent (même pattern que MarketplaceCategoryFiltersBar).
 */
export const CatalogHeaderCategoryFiltersBar: React.FC<
  CatalogHeaderCategoryFiltersBarProps
> = ({catWeb, filtersHook, facets}) => {
  const search = useSearchClient();
  const nomenclatureWeb = search.getNomenclatureWeb();

  if (!nomenclatureWeb || !catWeb) return null;

  const currentCategory = nomenclatureWeb.find((cat) => cat.id === catWeb);
  if (!currentCategory) return null;

  // Catégorie parente
  const parentCategory =
    currentCategory.id_parent &&
    currentCategory.id_parent !== currentCategory.id
      ? nomenclatureWeb.find((cat) => cat.id === currentCategory.id_parent)
      : null;

  // Enfants de la catégorie courante
  const childCategories = nomenclatureWeb.filter(
    (cat) => cat.id_parent === catWeb && cat.id !== catWeb,
  );

  // Filtrer par les facettes (ne montrer que les catégories ayant des résultats)
  const categoryIdsWithResults = facets?.['cat_web']?.values
    ? new Set(Object.keys(facets['cat_web'].values))
    : null;

  // Si les facettes sont disponibles, on ne garde que les sous-catégories ayant au moins 1 produit.
  // Sinon (facettes pas encore chargées), on affiche toutes les sous-catégories en attendant.
  const displayCategories = categoryIdsWithResults
    ? childCategories.filter((cat) => categoryIdsWithResults.has(cat.id))
    : childCategories;

  // Rien à afficher : ni bouton retour parent, ni sous-catégories
  if (!parentCategory && displayCategories.length === 0) return null;

  const handleFilterClick = (categoryId: string) => {
    filtersHook.change('cat_web', categoryId);
  };

  return (
    <Flex
      flexWrap='wrap'
      gap={2}
      px='20px'
      pt='20px'
      pb={3}
    >
      {/* Bouton retour vers le parent */}
      {parentCategory && (
        <Button
          leftIcon={<ChevronLeftIcon />}
          bg='blue.main'
          color='white'
          borderWidth={1}
          borderColor='blue.main'
          onClick={() => {
            handleFilterClick(parentCategory.id);
          }}
          size='sm'
          borderRadius='full'
          whiteSpace='nowrap'
          minW='auto'
          px={{base: 3, sm: 4}}
          _hover={{bg: 'blue.deep'}}
        >
          {capitalizeFirstLetter(parentCategory.libelle)}
        </Button>
      )}

      {/* Sous-catégories */}
      {displayCategories.map((cat) => (
        <Button
          key={cat.id}
          variant='outline'
          bg='white'
          color='gray.dark'
          borderWidth={1}
          borderColor='gray.200'
          onClick={() => {
            handleFilterClick(cat.id);
          }}
          size='sm'
          borderRadius='full'
          whiteSpace='nowrap'
          minW='auto'
          px={{base: 3, sm: 4}}
          _hover={{bg: 'gray.50'}}
        >
          {capitalizeFirstLetter(cat.libelle)}
        </Button>
      ))}
    </Flex>
  );
};
